/**
 * ComposerHint — a small info (ⓘ) icon under the prompt box. Click to reveal a
 * popover with: (1) optional LIVE room guidance — suggested next prompts +
 * completion status, sourced from the room-context synthesis (CONTEXT.md §13a /
 * §15d); and (2) the static prompt tips (keyboard shortcuts). Guidance is
 * advisory only — clicking a suggestion just fills the prompt box; the user is
 * free to ignore it and type anything.
 *
 * Uses the mandated PortalMenu (opens above; composer is at the bottom).
 *
 * @param {{
 *   bullets?: React.ReactNode[],          // static tips (fallback / always shown)
 *   label?: string,
 *   guidance?: {                          // live room guidance, or null
 *     suggestedPrompts?: string[],
 *     completion?: { pct?: number|null, label?: string, basis?: string },
 *   } | null,
 *   onUsePrompt?: (text: string) => void, // fill the composer with a suggestion
 *   guidanceAsOf?: string,                // ISO timestamp of the synthesis
 * }} props
 */
import React, { useRef, useState, useCallback } from 'react';
import { Info, Sparkles, ArrowUpRight } from 'lucide-react';
import { PortalMenu } from './PortalMenu.jsx';

export default function ComposerHint({
  bullets = [],
  label = 'Prompt tips',
  testId = 'composer-hint',
  // ``inline`` drops the centering/margin wrapper so the trigger can sit inside
  // an existing row (e.g. the composer's under-bar line) instead of its own row.
  inline = false,
  guidance = null,
  onUsePrompt,
  guidanceAsOf = '',
  // Optional node rendered at the TOP of the popover (e.g. the project pipeline stepper). Presence
  // alone makes this a guidance (✦) trigger.
  pipeline = null,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // Suggested-next prompts are an ACTION surface — only meaningful when a handler can consume a
  // click. In context-only mode (no ``onUsePrompt``, e.g. the reconciled composer where the
  // deterministic proposal owns "what's next") we suppress them and show premise + completion only.
  const prompts = (onUsePrompt && Array.isArray(guidance?.suggestedPrompts))
    ? guidance.suggestedPrompts.filter((p) => String(p || '').trim())
    : [];
  const completion = guidance?.completion || null;
  const premise = String(guidance?.workingPremise || '').trim();
  const hasGuidance = prompts.length > 0 || !!pipeline
    || (completion && (completion.label || completion.pct != null)) || !!premise;

  // Nothing to show at all → render nothing (preserves prior behaviour).
  if (!bullets.length && !hasGuidance) return null;

  const pct = completion && typeof completion.pct === 'number' ? completion.pct : null;

  return (
    <div className={inline ? 'inline-flex' : 'mt-1.5 flex justify-center'}>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={hasGuidance ? 'Room guidance' : label}
        title={hasGuidance ? 'Room guidance — suggested next steps' : label}
        data-testid={testId}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
          hasGuidance
            ? 'text-indigo-500 hover:bg-indigo-50 hover:text-indigo-600'
            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
        }`}
      >
        {hasGuidance ? <Sparkles className="h-3.5 w-3.5" aria-hidden /> : <Info className="h-3.5 w-3.5" aria-hidden />}
      </button>

      <PortalMenu anchorRef={anchorRef} open={open} onClose={close} align="top-right" width={300} menuRole="dialog">
        {hasGuidance ? (
          <div className="border-b border-gray-100 px-3 py-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600">
                <Sparkles className="h-3 w-3" aria-hidden /> Room guidance
              </span>
              {guidanceAsOf ? (
                <span className="text-[10px] text-gray-400">as of last run</span>
              ) : null}
            </div>

            {/* Pipeline stepper (guided journey) — where the user is in Plan→Review→Finalize→Run. */}
            {pipeline ? <div className="mb-2 flex justify-center" data-testid="composer-hint-pipeline">{pipeline}</div> : null}

            {/* Completion — shown as a label (+ optional bar). Advisory; pct may be null. */}
            {completion && (completion.label || pct != null) ? (
              <div className="mb-2">
                <div className="flex items-center justify-between text-[11px] text-gray-600">
                  <span className="font-medium text-gray-700">{completion.label || 'Progress'}</span>
                  {pct != null ? <span className="text-gray-500">{pct}%</span> : null}
                </div>
                {pct != null ? (
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-indigo-400" style={{ width: `${pct}%` }} />
                  </div>
                ) : null}
                {completion.basis ? (
                  <p className="mt-1 text-[10px] leading-snug text-gray-400">{completion.basis}</p>
                ) : null}
              </div>
            ) : null}

            {/* Working premise — the room's established canon/spine. When inferred
                (no uploaded source) it's the "virtual synopsis"; showing it lets
                the user correct a wrong inference in one prompt (CONTEXT.md §15f). */}
            {premise ? (
              <div className="mb-2 rounded-md bg-gray-50 px-2 py-1.5">
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Working premise</span>
                <p className="mt-0.5 text-[11px] leading-snug text-gray-600">{premise}</p>
              </div>
            ) : null}

            {/* Suggested next prompts — click to fill the composer (Tab-free path). */}
            {prompts.length ? (
              <div className="space-y-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Suggested next</span>
                {prompts.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { if (onUsePrompt) onUsePrompt(p); close(); }}
                    className="group flex w-full items-start gap-1.5 rounded-md border border-gray-200 px-2 py-1.5 text-left text-[11px] text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <ArrowUpRight className="mt-0.5 h-3 w-3 shrink-0 text-gray-300 group-hover:text-indigo-400" aria-hidden />
                    <span className="min-w-0">{p}</span>
                  </button>
                ))}
                <p className="pt-0.5 text-[10px] text-gray-400">Advisory — pick one or type your own.</p>
              </div>
            ) : null}
          </div>
        ) : null}

        {bullets.length ? (
          <ul className="space-y-1 px-3 py-2 text-xs text-gray-600">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="select-none text-gray-300">•</span>
                <span className="min-w-0">{b}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </PortalMenu>
    </div>
  );
}
